import React, { memo, useCallback, useEffect } from "react";
import { TouchableWithoutFeedback, Vibration } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import { useState } from "react";
import { Box, MainText, PressableOpacity } from "app/components/common";
import { likeMessage } from "@microdoing/shared/services/message/actions";
import AudioMessage from "../AudioMessage";
import ImageMessage from "../ImageMessage";
import VideoMessage from "../VideoMessage";
import Animated, { Easing } from "react-native-reanimated";
import i18n from "@microdoing/shared/i18n";
import theme from "app/Theme/Theme";
import { isWeb } from "@microdoing/shared/utils/deviceDetectors";
import LikeLabel from "./LikeLabel";

import { Keyframe } from "react-native-reanimated";
import Avatar from "../../Avatar";
import PictureName from "app/components/common/PictureName";

const keyframe = new Keyframe({
  0: {
    transform: [{ translateY: 15 }],
  },
  50: { transform: [{ translateY: 10 }] },
  100: {
    transform: [{ translateY: 0 }],
    easing: Easing.ease,
  },
});

const ReanimatedBox = Animated.createAnimatedComponent(Box);

export const CustomBubble = memo(
  ({
    handlePressAvatar,
    currentMessage,
    nextMessage,
    setFocusedMessage,
    displayUnsend,
    ...props
  }) => {
    const [lastPress, setLastPress] = useState(0);
    const userId = useSelector(getUserId);
    const isMyMessage = userId === currentMessage?.user?._id;
    const likedBy = currentMessage?.likedBy;
    const isLiked = likedBy?.length > 0;
    const messageId = currentMessage?._id;
    const dispatch = useDispatch();
    const isMedia =
      currentMessage?.audio || currentMessage?.video || currentMessage?.image;

    const nextMessageIsNotFromTheSameUser =
      nextMessage?.user?._id !== currentMessage?.user?._id;

    const onLikeMessage = () => {
      const currentTime = new Date().getTime();
      const delta = currentTime - lastPress;

      if (delta < 300) {
        dispatch(likeMessage({ messageId, userId }));
      }
      setLastPress(currentTime);
    };

    const handleLongPress = () => {
      if (isMyMessage) {
        setFocusedMessage(currentMessage);
        displayUnsend();
      }
    };

    if (currentMessage?.expireAt)
      return (
        <ReanimatedBox
          entering={keyframe}
          justifyContent={isMyMessage ? "flex-end" : "flex-start"}
          width={"100%"}
          paddingRight={isMyMessage ? "xs" : null}
          paddingLeft={isMyMessage ? null : "xs"}
          alignItems={isMyMessage ? "flex-end" : "flex-start"}
        >
          <Box
            maxWidth="70%"
            borderRadius={8}
            paddingVertical={currentMessage.audio ? "xxs" : isMedia ? 0 : "xs"}
            paddingHorizontal={currentMessage.audio ? "s" : isMedia ? 0 : "s"}
            marginBottom={"xxs"}
            backgroundColor="neutral.white"
            borderColor="neutral.300"
            borderWidth={1}
          >
            <MainText color="neutral.400">
              {i18n.t("you-unsent-a-message")}
            </MainText>
          </Box>
        </ReanimatedBox>
      );

    return (
      <ReanimatedBox
        overflow="visible"
        entering={keyframe.duration(300)}
        justifyContent={isMyMessage ? "flex-end" : "flex-start"}
        width={"100%"}
        paddingRight={isMyMessage ? "xs" : null}
        paddingLeft={isMyMessage ? null : "xs"}
        alignItems={isMyMessage ? "flex-end" : "flex-start"}
      >
        <TouchableWithoutFeedback
          style={{ overflow: "visible" }}
          onPress={onLikeMessage}
          onLongPress={handleLongPress}
        >
          <Box
            flexDirection="row"
            overflow="visible"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            {!isMyMessage && nextMessageIsNotFromTheSameUser ? (
              <PressableOpacity
                onPress={handlePressAvatar(currentMessage?.user?._id)}
              >
                <PictureName
                  name={currentMessage?.user?.name}
                  imageUrl={currentMessage?.user?.avatar}
                  pictureSize={24}
                  style={{
                    alignSelf: "flex-end",
                    marginRight: theme.spacing.s,
                    marginBottom: isLiked
                      ? theme.spacing.sm
                      : theme.spacing.xxs,
                  }}
                />
              </PressableOpacity>
            ) : (
              <Box
                style={{
                  width: 24,
                  height: 24,
                  marginRight: theme.spacing.s,
                  marginBottom: isLiked ? theme.spacing.sm : theme.spacing.xxs,
                }}
              />
            )}
            <Box
              overflow="visible"
              maxWidth="70%"
              borderRadius={8}
              paddingVertical={
                currentMessage?.audio ? "xxs" : isMedia ? 0 : "xs"
              }
              paddingHorizontal={
                currentMessage?.audio ? "s" : isMedia ? 0 : "s"
              }
              marginBottom={isLiked ? "sm" : "xxs"}
              backgroundColor={
                isWeb && currentMessage?.audio
                  ? "neutral.white"
                  : isMyMessage
                  ? "brand.500"
                  : "neutral.75"
              }
            >
              {currentMessage?.audio ? (
                <AudioMessage
                  hasDarkBackground={isMyMessage}
                  soundUrl={currentMessage?.audio}
                />
              ) : currentMessage?.video ? (
                <VideoMessage {...props} />
              ) : currentMessage?.image ? (
                <ImageMessage imageUrl={currentMessage?.image} />
              ) : (
                <MainText
                  variant="l4-medium"
                  color={isMyMessage ? "neutral.white" : "neutral.900"}
                >
                  {currentMessage?.text}
                </MainText>
              )}
              {isLiked && <LikeLabel likedBy={likedBy} />}
            </Box>
          </Box>
        </TouchableWithoutFeedback>
      </ReanimatedBox>
    );
  },
);
